import type { HelpSectionsMap } from './types';

export const ruHelpSections: HelpSectionsMap = {
  start: {
    title: 'Начало работы',
    intro: 'Краткий обзор для начала работы в WERSTACK. Пройдите шаги по порядку — так проще разобраться.',
    blocks: [
      {
        title: 'Шаг 1. Выберите рабочее пространство',
        text: 'Рабочее пространство (workspace) — это ваш «проект» с отдельными файлами, настройками и участниками.',
        items: [
          'В боковой панели слева откройте раздел «Мои» или «Общие» и выберите нужное пространство.',
          'Файлы и папки переключатся автоматически.',
          'Кнопка «Создать» вверху sidebar — новое своё пространство (если не достигнут лимит).',
          'У каждого пространства могут быть свои язык, тема и акцентный цвет.',
        ],
        where: 'Боковая панель слева → «Мои» / «Общие». Если sidebar свёрнут — наведите на иконку раздела для списка.',
      },
      {
        title: 'Шаг 2. Загрузите первые файлы',
        items: [
          'Нажмите «Загрузить изображения» в панели инструментов галереи.',
          'Ctrl+Shift+М — вставить скриншот из буфера обмена в текущую папку.',
          'Можно выбрать до 1000 файлов за раз (JPEG, PNG, WebP; до 10 МБ каждый).',
          'Внизу экрана появится полоска прогресса загрузки — её можно закрыть или отменить.',
        ],
        where: 'Главный экран → кнопка «Загрузить изображения» справа вверху.',
      },
      {
        title: 'Шаг 3. Создайте папки',
        items: [
          'Нажмите «Создать папку», введите имя и подтвердите.',
          'Откройте папку двойным щелчком (на телефоне — один тап).',
          'В корне пространства слева в панели инструментов показано его название.',
          'Во вложенной папке — хлебные крошки с иконкой «домой» для возврата в корень.',
        ],
        where: 'Главный экран → кнопка «Создать папку» рядом с загрузкой.',
      },
      {
        title: 'Боковая панель',
        items: [
          'Логотип WERSTACK и списки пространств — вверху.',
          '«Создать» — новое пространство.',
          '«Данные» — таблицы: «Все» открывает каталог; списки ниже — для быстрого открытия.',
          '«Доски» — kanban поверх таблиц.',
          '«Процессы» — графы автоматизации текущего пространства.',
          '«Галереи» — каталог публичных галерей.',
          'Кнопка на границе панели — свернуть или развернуть sidebar.',
        ],
        where: 'Левая колонка главного экрана.',
      },
      {
        title: 'Кнопки в верхней панели',
        items: [
          'Колокольчик — приглашения в команду и уведомления о комментариях.',
          'Знак вопроса — эта справка (открывается отдельно от профиля).',
          'Инициалы — выход из аккаунта.',
        ],
        where: 'Правый верхний угол экрана.',
      },
      {
        title: 'Роли и права',
        text: 'Не все действия доступны каждому пользователю.',
        items: [
          'Владелец и Админ — полный доступ, управление участниками и галереями.',
          'Редактор — просмотр и изменение файлов, без управления командой.',
          'Наблюдатель — только просмотр, без загрузки и удаления.',
        ],
        tip: 'Если кнопка неактивна или пункта меню нет — скорее всего, у вас роль «Наблюдатель».',
      },
    ],
  },
  gallery: {
    title: 'Галерея: файлы и папки',
    intro: 'Основной экран, где хранятся и организуются ваши изображения.',
    blocks: [
      {
        title: 'Панель инструментов',
        items: [
          'Слева — название пространства (в корне), хлебные крошки (во вложенной папке) или индикатор активного поиска/фильтра.',
          'Справа — загрузка, создание папки, поиск, фильтр по тегам, сортировка, вид сетка/список, корзина.',
        ],
        where: 'Полоса под верхней панелью приложения.',
      },
      {
        title: 'Виды отображения',
        items: [
          'Сетка — карточки с превью, лайками и значком комментариев.',
          'Список — таблица: имя, дата, тип, размер; удобно для массового выделения.',
        ],
        where: 'Кнопка переключения вида (иконки сетки и списка) справа в панели инструментов.',
      },
      {
        title: 'Поиск файлов',
        items: [
          'Нажмите иконку лупы и введите минимум 2 символа.',
          'Поиск ищет по имени файла, тегам и тексту комментариев.',
          'Чтобы выйти из поиска — нажмите «Сбросить» или очистите поле.',
        ],
        where: 'Иконка лупы в панели инструментов. При активном поиске сортировка и загрузка скрыты.',
      },
      {
        title: 'Фильтр по тегам',
        items: [
          'Нажмите иконку тега и выберите один или несколько тегов.',
          'Выбранные теги отображаются чипами слева — каждый можно убрать крестиком.',
          'При активных тегах появляется кнопка «Создать галерею по тегам» (см. раздел «Публичные галереи»).',
        ],
        where: 'Иконка тега в панели инструментов.',
      },
      {
        title: 'Сортировка',
        items: [
          'По дате загрузки (сначала новые или старые).',
          'По имени (А–Я или Я–А).',
          'По размеру файла.',
          'По количеству лайков.',
        ],
        where: 'Иконка ползунков в панели инструментов. Недоступна в корзине, при поиске и фильтре по тегам.',
      },
      {
        title: 'Лайки',
        items: [
          'Нажмите сердечко на карточке, чтобы поставить или убрать лайк.',
          'Лайк не открывает файл — для открытия нажмите на превью или имя.',
        ],
      },
      {
        title: 'Страницы',
        text: 'Если файлов много, внизу галереи можно листать страницы и менять число элементов на странице.',
        where: 'Панель пагинации внизу экрана.',
      },
    ],
  },
  selection: {
    title: 'Выделение и действия',
    intro: 'Как выбирать несколько файлов и папок и что с ними можно сделать.',
    blocks: [
      {
        title: 'Выделение мышью',
        items: [
          'Клик по элементу — добавить или убрать из выделения.',
          'Shift + клик — выделить диапазон от последнего выбранного.',
          'Перетащите рамку по пустому месту — выделить всё внутри рамки.',
          'Ctrl (Cmd на Mac) + рамка — добавить к уже выделенному.',
          'Ctrl+A — выделить всё на странице.',
          'Escape или клик вне галереи — снять выделение.',
        ],
      },
      {
        title: 'Выделение на сенсорном экране',
        items: [
          'Один тап — открыть файл или папку.',
          'Двойной тап по карточке — добавить в выделение.',
          'Проведите пальцем по пустому месту (рамка) — массовое выделение.',
          'Долгое нажатие на пустом месте — открыть контекстное меню.',
        ],
      },
      {
        title: 'Перетаскивание (drag and drop)',
        items: [
          'Зажмите файл или папку и перетащите на другую папку — элемент переместится.',
          'Если элемент уже в выделении, перетащится всё выделение целиком.',
          'На телефоне работает перетаскивание пальцем с превью-карточкой.',
        ],
        tip: 'В корзине перетаскивание отключено.',
      },
      {
        title: 'Вырезать и вставить',
        items: [
          'Выделите элементы → «Вырезать» в контекстном меню или Ctrl+X.',
          'Перейдите в целевую папку → «Вставить» или Ctrl+V.',
          'Нельзя вставить в ту же папку, откуда вырезали.',
        ],
      },
      {
        title: 'Контекстное меню',
        text: 'Правый клик по галерее (или долгое нажатие на телефоне) открывает меню действий.',
        items: [
          'Загрузить изображения / Создать папку.',
          'Вырезать, Вставить.',
          'Переименовать папку (ровно одна папка в выделении).',
          'Создать галерею (ровно одна папка в выделении).',
          'Добавить тег (только файлы в выделении).',
          'Сгенерировать — выделенные изображения (редакторы и админы).',
          'Удалить (перемещает в корзину).',
          'Восстановить (только в корзине).',
        ],
        tip: 'Рядом с пунктами меню показаны горячие клавиши.',
      },
      {
        title: 'Массовые операции',
        items: [
          'Удалить выделенное — в корзину (с подтверждением).',
          'Добавить тег — ко всем выделенным файлам сразу.',
          'Вырезать и вставить — переместить группу файлов.',
        ],
      },
      {
        title: 'AI-генерация (галерея)',
        items: [
          'Выделите одно или несколько изображений → контекстное меню → «Сгенерировать».',
          'По одному новому изображению на каждый выбранный файл, по очереди. Скачайте или сохраните результат — исходники не меняются.',
        ],
      },
    ],
  },
  viewer: {
    title: 'Просмотр файла',
    intro: 'Полноэкранное окно с информацией о файле, комментариями и действиями.',
    blocks: [
      {
        title: 'Как открыть',
        items: [
          'Нажмите на карточку файла в галерее (или на имя в режиме списка).',
          'В режиме массового выделения — двойной клик / двойной тап.',
        ],
      },
      {
        title: 'Навигация между файлами',
        items: [
          'Стрелки «Предыдущий» и «Следующий» по бокам окна.',
          'Клавиши ← и → на клавиатуре.',
          'Escape — закрыть окно (или выйти из полноэкранного просмотра изображения).',
        ],
      },
      {
        title: 'Вкладка «Информация»',
        items: [
          'Имя, разрешение, размер, дата загрузки.',
          'Теги — клик по тегу отфильтрует галерею по этому тегу.',
          'Кнопки: Редактировать, Скачать, Создать/отозвать публичную ссылку, Скопировать URL.',
        ],
        where: 'Правая панель модального окна → вкладка «Информация».',
      },
      {
        title: 'Теги',
        items: [
          'Иконка тега в углу окна — добавить новый тег.',
          'Крестик на чипе тега — удалить тег.',
        ],
      },
      {
        title: 'AI-генерация изображения',
        items: [
          'Иконка «искры» рядом с тегом — опишите фон и сгенерируйте новое изображение товара на основе текущего файла.',
          'Превью остаётся в модалке: скачайте или сохраните как новый файл в папку (исходник не перезаписывается).',
        ],
        where: 'Панель просмотрщика → иконка искр (редакторы и админы).',
      },
      {
        title: 'Публичная ссылка на файл',
        items: [
          '«Создать публичную ссылку» — любой с ссылкой сможет просмотреть файл без входа.',
          '«Отозвать ссылку» — доступ прекратится.',
          '«Скопировать URL» — скопировать ссылку в буфер.',
        ],
        tip: 'Это ссылка на один файл. Для альбома изображений используйте «Публичные галереи».',
      },
      {
        title: 'Вкладка «Комментарии»',
        items: [
          'Пишите комментарии и ответы к файлу.',
          'Редактируйте и удаляйте свои комментарии.',
          'Ставьте реакции-эмодзи на комментарии.',
          'Упоминайте коллег через @ — начните вводить имя или email.',
        ],
        where: 'Правая панель → вкладка «Комментарии». Уведомления о новых комментариях — в колокольчике.',
      },
      {
        title: 'Полноэкранный просмотр',
        text: 'Нажмите иконку развернуть на изображении, чтобы раскрыть его на весь экран.',
      },
    ],
  },
  editor: {
    title: 'Редактор изображений',
    intro: 'Откройте файл из просмотрщика кнопкой «Редактировать». Холст собирается из слоёв: макет, изображения, текст и фигуры. Все изменения можно отменить.',
    blocks: [
      {
        title: 'Верхняя панель',
        items: [
          'Увеличить / уменьшить / вписать в экран — масштаб холста.',
          'Отменить / Повторить — шаги истории правок.',
          'Сброс — вернуть исходное изображение.',
          'Показаны текущий масштаб (%) и размер макета в пикселях.',
        ],
      },
      {
        title: 'Слои и свойства',
        items: [
          'Панель слоёв (слева) — макет, изображения, текст и фигуры; выбор, порядок, блокировка, удаление.',
          'Панель свойств (справа) — настройки выбранного слоя.',
          'Нажмите на слой на холсте или в списке, чтобы редактировать его.',
          'Добавить изображение — дополнительные слои из вашего пространства.',
          'Ctrl+C (Ctrl+С) — копировать выбранный слой (изображение, стикер, текст или фигуру). Макет копировать нельзя.',
          'Ctrl+V (Ctrl+М) — вставить скопированный слой со смещением.',
        ],
        where: 'Кнопки с иконками слоёв и ползунков на панели инструментов.',
      },
      {
        title: 'Макет (frame)',
        items: [
          'Размер, фон, скругления углов и обводка.',
          'Задать пропорцию — пресеты соотношения сторон (1:1, 16:9, 4:3 и др.) для макета.',
          'При смене пропорций макета текст и фигуры остаются на месте и не меняют размер — меняется только видимая область макета.',
          'Слой изображения сохраняет визуальную позицию; после смены пропорций его можно спокойно масштабировать и перемещать.',
        ],
        where: 'Панель свойств (справа), когда выбран слой макета.',
        tip: 'Escape — выйти из режима пропорций макета.',
      },
      {
        title: 'Слой изображения',
        items: [
          'Выберите слой изображения — в панели свойств откроется раздел «Изображение».',
          'Позиция и размер — поля с числами; поворот — ползунок (−180°…+180°) в секции «Поворот».',
          'Правый клик по изображению → режим изменения размера; тяните за углы. Перетаскивание внутри изображения — сдвиг в пределах макета.',
          'Секция «Трансформация» — «Редактирование с ИИ» (промпт внизу холста, до 200 символов), «Удалить фон» (списывает из общего пула AI-токенов), выпрямление (ползунок угла внизу экрана, затем «Готово»), отразить по горизонтали / вертикали.',
          'Секция «Трансформация» — «Обрезать» (произвольный прямоугольник + «Вырезать»), «Лассо» (удалить выделенное или оставить только выделенное + галочка для применения).',
          'Секции «Цвет» и «Эффекты» — яркость, контраст, насыщенность и пресеты фильтров.',
        ],
        where: 'Панель свойств (справа), когда выбран слой изображения.',
        tip: '«Редактирование с ИИ» отправляет текущий вид слоя (поворот, отражение, фильтры) и подставляет результат на холст. Escape — выйти из режима изменения размера, выпрямления, обрезки, лассо или ввода промпта. На телефоне двойной тап замыкает контур лассо.',
      },
      {
        title: 'Стикеры',
        items: [
          'Кнопка «Добавить стикер» на панели инструментов открывает библиотеку стикеров вашего пространства.',
          'Новый стикер появляется после «Удалить фон» у слоя изображения — результат сохраняется в библиотеку и подставляется в текущий слой.',
          'Нажмите на стикер в панели или перетащите его на холст — он добавится как отдельный слой.',
          'Стикер редактируется как изображение: позиция, размер, поворот, тень; в панели слоёв отмечен отдельной иконкой.',
          'Крестик на карточке в библиотеке — удалить стикер из пространства безвозвратно (слои на макете не затрагивает).',
        ],
        where: 'Панель инструментов → иконка стикера; панель стикеров слева (рядом с панелью слоёв).',
        tip: 'Удаление фона учитывает текущие трансформации слоя: поворот, отражение, масштаб и фильтры.',
      },
      {
        title: 'Текстовые слои',
        items: [
          'Добавьте заголовок или обычный текст с панели инструментов.',
          'Шрифт, размер, цвет, обводка, тень, фон, интервалы.',
          'Выравнивание — по горизонтали (лево / центр / право) и по вертикали (верх / центр / низ) внутри рамки текста.',
          'Перетаскивайте текст на холсте; меняйте размер рамки за углы.',
          '«Применить к изображению» — вшить текст в растр перед сохранением.',
        ],
        where: 'На телефоне панель свойств открывается кнопкой с иконкой ползунков.',
      },
      {
        title: 'Фигуры',
        items: [
          'Прямоугольник, треугольник, круг, линия — выберите инструмент и протяните на холсте.',
          'Заливка, обводка, скругления, тень и градиент — в свойствах.',
          'Перетаскивание — перемещение; углы — размер; верхняя ручка — поворот.',
          'Escape отменяет активный инструмент рисования.',
        ],
      },
      {
        title: 'Навигация по холсту',
        items: [
          'Мышь: перетаскивание — панорама; колесо — зум.',
          'Телефон: pinch — зум; один палец — панорама.',
        ],
      },
      {
        title: 'Сохранение',
        items: [
          'Сохранить — перезаписать исходный файл.',
          'Скачать — сохранить файл на компьютер.',
          'Сохранить как — новый файл (имя, формат PNG/JPG/WebP, размер, папка).',
        ],
        where: 'Кнопка «Сохранить» и меню со стрелкой справа в шапке редактора.',
      },
    ],
  },
  publicGalleries: {
    title: 'Публичные галереи',
    intro: 'Красивые страницы для показа работ клиентам — без входа в аккаунт.',
    blocks: [
      {
        title: 'Галерея из папки',
        items: [
          'Выделите ровно одну папку.',
          'Контекстное меню → «Создать галерею» (или Ctrl+Alt+G).',
          'Задайте название, макет, тему, данные автора.',
          'Скопируйте ссылку и отправьте клиенту.',
        ],
      },
      {
        title: 'Галерея по тегам',
        items: [
          'Включите фильтр по одному или нескольким тегам.',
          'Нажмите «Создать галерею по тегам».',
          'Состав галереи обновляется автоматически, когда вы меняете теги у файлов.',
        ],
        where: 'Кнопка появляется в панели инструментов при активном фильтре тегов.',
      },
      {
        title: 'Настройки галереи',
        items: [
          'Макет: сетка, masonry или крупные карточки.',
          'Тема: светлая/тёмная и акцентный цвет.',
          'Можно сгенерировать визитку с QR-кодом.',
        ],
      },
      {
        title: 'Панель автора',
        items: [
          'Режим «Автор» — имя, род деятельности, контакты и описание.',
          'Режим «Компания» — название, логотип из файлов пространства, слоган, контакты и «О компании».',
          'Переключение режима не стирает данные другого режима.',
          'Заполненные поля отображаются в правой панели публичной страницы и на цифровой визитке.',
        ],
        where: 'Раздел «Автор» в окне создания или редактирования галереи.',
      },
      {
        title: 'Управление галереями',
        items: [
          'Сайдбар → «Галереи» — каталог всех публичных галерей по пространствам.',
          'Открыть, редактировать, удалить или показать QR-визитку — действия на карточке.',
        ],
        where: 'Боковая панель → «Галереи» (страница /galleries).',
      },
      {
        title: 'Что видит посетитель',
        items: [
          'Сетку изображений с бесконечной прокруткой.',
          'Полноэкранный просмотр (Escape, стрелки ← →).',
          'Панель автора: данные человека или компании с контактами.',
          'Скачивание цифровой визитки с QR-кодом на галерею.',
        ],
      },
    ],
  },
  profile: {
    title: 'Профиль и команды',
    intro: 'Личные настройки, рабочие пространства и участники команды.',
    blocks: [
      {
        title: 'Как открыть профиль',
        items: [
          'Пункт «Профиль» в боковой панели внизу.',
          '«Выйти» — завершить сеанс (меню инициалов в правом верхнем углу).',
        ],
        tip: 'Профиль открывается отдельной страницей; справка (знак вопроса) — отдельная панель.',
      },
      {
        title: 'Вкладка «Личное»',
        items: [
          'Изменить имя и email.',
          'Нажмите «Сохранить изменения».',
        ],
      },
      {
        title: 'Вкладка «AI-токены»',
        items: [
          'Показывает использованные и оставшиеся токены общего пула (удаление фона, генерация изображений, AI-редактирование).',
          'Когда токены закончились, AI-действия недоступны, пока админ не повысит лимит.',
        ],
        where: 'Профиль → AI-токены.',
      },
      {
        title: 'Вкладка «Безопасность»',
        items: [
          'Сменить пароль: новый пароль и подтверждение.',
          'Пароль должен соответствовать требованиям сложности.',
        ],
      },
      {
        title: 'Вкладка «Пространства»',
        items: [
          'Создать новое workspace (имя, язык, тема, акцент).',
          'Редактировать или удалить своё пространство.',
          'Покинуть чужое пространство.',
          'Смотреть занятое место на диске.',
        ],
      },
      {
        title: 'Вкладка «Команды»',
        items: [
          'Список участников по каждому вашему пространству, со счётчиком занято / лимит (лимит включает владельца).',
          'Изменить роль: Админ, Редактор, Наблюдатель (⋮ → Редактировать роль).',
          'Назначить видимость: скрыть папки, таблицы или доски от участника (⋮ → Назначить видимость).',
          'Удалить участника из пространства.',
        ],
      },
      {
        title: 'Видимость',
        items: [
          'Видимость — запрет поверх роли: скрытый ресурс недоступен, даже если роль формально позволяет.',
          'Владелец может скрывать ресурсы от любого участника. Админ — только от Редактора и Наблюдателя, не от Владельца и не от другого Админа.',
          'Скрытие папки скрывает и вложенные папки с файлами в приложении (списки, поиск, открытие по ссылке в приложении).',
          'Таблицы и доски скрываются отдельно от папок.',
          'При приглашении: после выбора роли можно сразу задать, что скрыть, и отправить приглашение.',
          'Публичные ссылки на файлы продолжают работать, даже если файл в «скрытой» папке.',
          'Корневую папку пространства скрыть нельзя.',
        ],
        where: 'Профиль → Команды → ⋮ у участника → Назначить видимость. Или колокольчик → Отправленные → мастер приглашения.',
        tip: 'Правила видимости действуют только внутри этого пространства и не переносятся на другие.',
      },
    ],
  },
  datasets: {
    title: 'Таблицы',
    intro: 'Таблицы в стиле CRM в ваших пространствах — колонки, строки и редактирование прямо в ячейках.',
    blocks: [
      {
        title: 'Где найти таблицы',
        items: [
          'Сайдбар → «Данные» → «Все» — каталог всех таблиц по пространствам (страница /data).',
          'Сайдбар → «Данные»: быстрый список ваших и общих таблиц.',
          '«Создать» под «Данные» — новая таблица в текущем пространстве.',
          'На странице каталога — открыть, редактировать, удалить, экспортировать или схему — с карточки.',
        ],
        where: 'Левая панель → Данные (и «Все» для полного каталога).',
      },
      {
        title: 'Открытие таблицы',
        items: [
          'Клик по названию в сайдбаре или по карточке на /data — CRM-грид на /data/:id.',
          'При прокрутке подгружаются следующие строки (по 100).',
          'Позиция прокрутки сохраняется при возврате к той же таблице.',
        ],
      },
      {
        title: 'Колонки и схема',
        items: [
          '+ в шапке — добавить колонку (имя, тип, варианты).',
          'Перетаскивание за grip в заголовке — изменить порядок колонок.',
          'Перетаскивание правого края — ширина; сохраняется локально.',
          'Клик по названию колонки в заголовке — переименование (если можно редактировать схему).',
          'Меню ⋮ в заголовке — фильтр по тексту, сортировка по возрастанию или убыванию, закрепление колонки при горизонтальной прокрутке, сброс сортировки или фильтра, удаление колонки.',
          'Каталог /data → редактировать на карточке — имя, описание и колонки.',
        ],
      },
      {
        title: 'Сортировка и фильтрация',
        text: 'Для каждой таблицы сохраняются одна сортировка и один текстовый фильтр. Настройки хранятся в браузере и применяются при загрузке строк — включая бесконечную прокрутку и скачивание.',
        items: [
          'Меню ⋮ в заголовке колонки (на планшете и телефоне иконка всегда видна).',
          'Сортировка по возрастанию или убыванию — маленькая стрелка в названии колонки показывает направление.',
          'Сортировать можно только по одной колонке; сброс — пункт «Сбросить сортировку» в том же меню.',
          'Фильтр — введите текст и нажмите «Применить» (или Enter), чтобы оставить строки, где колонка содержит этот текст.',
          'При активном фильтре рядом с названием колонки показывается иконка фильтра (как у сортировки).',
          '«Сбросить фильтр» — убрать фильтр; смена сортировки или фильтра перезагружает строки с сервера.',
        ],
        where: 'Заголовок колонки → меню ⋮.',
        tip: 'Сортировка и фильтр сохраняются отдельно для каждой таблицы на этом устройстве и учитываются при экспорте из каталога таблиц.',
      },
      {
        title: 'Справочники',
        text: 'Справочник — это отдельная «таблица-словарь»: статусы, категории, артикулы и другие значения, которые вы хотите использовать в нескольких местах, а не копировать вручную.',
        items: [
          'Создать: сайдбар → «Данные» → «Создать» → включите «Создать справочник». Можно сразу заполнить вручную или импортировать из CSV/Excel.',
          'Внутри — одна колонка с уникальными значениями. Работает как обычный грид: добавляйте, правьте и удаляйте строки прямо в таблице.',
          'Каталог /data → карандаш у справочника: имя, описание, переименование ключевой колонки, блок «Значения справочника».',
          'Там же кнопка скачивания — выгрузка с учётом фильтра и сортировки, если вы их настраивали в гриде.',
          'Импорт в уже существующий справочник: откройте его и выберите «Импорт CSV/Excel» — укажите колонку файла с ключами; повторы в файле пропускаются.',
          'В сайдбаре и на странице каталога справочники помечены бейджем «List».',
        ],
        where: 'Создание: сайдбар → Данные → «Создать». Управление: сайдбар → Данные → «Все» (/data).',
        tip: 'Один справочник на пространство — один источник правды для статусов, сезонов, SKU и т. п.',
      },
      {
        title: 'Колонка «Справочник» в таблице',
        text: 'Когда в обычной таблице нужно выбрать значение не «из головы», а из общего списка — добавьте колонку типа «Справочник» и привяжите её к нужному справочнику.',
        items: [
          '«Список» (select) — варианты живут только в этой колонке. Удобно для пары локальных значений.',
          '«Справочник» (reference) — значения берутся из отдельного справочника workspace. Удобно, когда один и тот же набор нужен в нескольких таблицах.',
          'Добавить колонку: «+» в шапке грида или каталог /data → редактировать таблицу → «Добавить колонку» → тип «Справочник» → выберите справочник из списка.',
          'В ячейке — выпадающий список с поиском по значениям справочника. После сохранения строки значение остаётся привязанным к записи в справочнике.',
          'Если переименуете значение в справочнике, в таблице отобразится актуальный текст.',
        ],
        where: 'Шапка таблицы → «+» или Данные → «Все» → редактирование на карточке.',
        tip: 'Сначала создайте и наполните справочник, потом добавляйте колонку в рабочие таблицы.',
      },
      {
        title: 'Справочник и файлы',
        text: 'Можно связать строку таблицы с файлами в галерее и автоматически ставить теги на файлы, когда меняется значение в колонке «Справочник».',
        items: [
          'Шаг 1 — «Связь с файлами»: откройте таблицу → кнопка «Связь с файлами» → выберите колонку, по которой искать файлы (обычно артикул или код), и режим сопоставления с тегами.',
          'После настройки в гриде появится колонка с превью связанных файлов; из просмотрщика изображения видны связанные строки.',
          'Шаг 2 — «Действие с файлами» на reference-колонке: при добавлении или редактировании колонки «Справочник» выберите «Добавить тег» или «Заменить тег в списке».',
          'Срабатывает, когда у строки уже есть связанные файлы и вы меняете значение в этой колонке: тег на файлах обновится автоматически (имя тега = значение из справочника).',
          'Без «Связи с файлами» действие с тегами не запустится — сначала настройте связь таблицы с галереей.',
        ],
        where: 'Таблица → «Связь с файлами»; действие с тегами — в настройках колонки «Справочник».',
        tip: 'Типичный сценарий: артикул связывает строку с фото товара, а смена статуса в справочнике автоматически проставляет тег «В продаже» или «Архив».',
      },
      {
        title: 'Объединение таблиц',
        text: 'Объедините две существующие таблицы в просмотр только для чтения. Строки связываются по колонкам — удобно для подтягивания данных из другой таблицы.',
        items: [
          'Сайдбар → «Данные» → «Объединить» — мастер из трёх шагов.',
          'Шаг 1: имя и пространство для объединённой таблицы.',
          'Шаг 2: основная и дополнительная таблица, затем колонки связи (в дополнительной колонка связи должна быть уникальной).',
          'Шаг 3: какие колонки из каждой таблицы показывать в результате.',
          'Объединённые таблицы открываются как обычный грид, но строки и схему нельзя редактировать — только порядок колонок, фильтры и сортировку.',
          'В сайдбаре и на каталоге /data у объединённых таблиц отображается бейдж «Merge».',
        ],
        where: 'Левая панель → Данные → Объединить (рядом с «Создать»).',
        tip: 'Сначала подготовьте обе исходные таблицы и общую колонку-ключ (например, артикул или ID), отмеченную как уникальную во второй таблице.',
      },
      {
        title: 'Строки и ячейки',
        items: [
          'Клик по ячейке — редактирование; Enter сохраняет, Escape отменяет.',
          'Tab / Shift+Tab — следующая / предыдущая ячейка.',
          '«Добавить строку» внизу — новая строка (пустой черновик отменяется кликом снаружи).',
          'Корзина в строке — удалить одну строку.',
          'Чекбоксы + ⋮ в шапке — массовое удаление выбранных строк.',
        ],
        tip: 'Изменения видны сразу; синхронизация с сервером идёт в фоне.',
      },
      {
        title: 'Права доступа',
        items: [
          'Владелец / Админ — схема, строки и метаданные.',
          'Редактор — только строки.',
          'Наблюдатель — только просмотр.',
        ],
      },
    ],
  },
  boards: {
    title: 'Доски',
    intro: 'Kanban-доски поверх таблиц: колонки — значения справочника (статусы), карточки — строки таблицы.',
    blocks: [
      {
        title: 'Где найти доски',
        items: [
          'Сайдбар → «Доски» → «Все» — каталог досок по пространствам (страница /boards).',
          'Сайдбар → «Доски»: быстрый список своих и общих досок.',
          '«Создать» под «Доски» — новая доска в текущем пространстве.',
          'В каталоге — открыть, изменить настройки или удалить через меню карточки.',
        ],
        where: 'Левый сайдбар → Доски (и «Все» для полного каталога).',
      },
      {
        title: 'Создание доски',
        items: [
          'Шаг 1: название, описание (необязательно) и пространство.',
          'Шаг 2: подходящая таблица и reference-колонка статусов; отметьте значения, которые станут колонками (минимум одно).',
          'Шаг 3: поле заголовка и дополнительные поля на карточке.',
          'После создания открывается страница доски /boards/:id.',
        ],
        tip: 'Подходят только обычные таблицы с reference-колонкой. Merge и списки (list) в мастере не предлагаются.',
      },
      {
        title: 'Kanban-доска',
        items: [
          'Колонки идут в порядке статусов доски; перетащите ручку колонки, чтобы изменить порядок (админ).',
          'На карточке — заголовок и несколько полей превью.',
          'Иконка сообщения с числом появляется на карточке, если есть комментарии.',
          'Иконка скрепки с числом появляется, если к карточке прикреплены файлы.',
          'Перетаскивайте карточки между колонками или внутри колонки — меняются статус и порядок.',
          'Живые обновления по SSE — другие пользователи видят перемещения, правки, комментарии и прикрепление/открепление файлов без перезагрузки.',
        ],
      },
      {
        title: 'Карточки',
        items: [
          'Клик по карточке — модалка просмотра: поля карточки, вкладки «Комментарии» и «Файлы».',
          '⋮ → Редактировать — боковая панель строки таблицы.',
          '⋮ → Удалить — удаляет строку из таблицы после подтверждения.',
          '«Добавить карточку» под колонкой — новая строка с этим статусом.',
        ],
        where: 'Страница доски → карточка или подвал колонки.',
        tip: 'Ссылка /boards/:id?card=:rowId открывает ту же модалку (например из колокольчика).',
      },
      {
        title: 'Комментарии к карточкам',
        items: [
          'Комментарии привязаны к этой доске и карточке (строке), а не ко всем доскам одной таблицы.',
          'Писать могут все с доступом к пространству (viewer+); чужие комментарии удаляет автор или админ.',
          'Новые комментарии приходят участникам пространства в колокольчик.',
        ],
      },
      {
        title: 'Файлы на карточках',
        items: [
          'Вкладка «Файлы» — прикрепите изображения из workspace доски (тот же picker, что в редакторе).',
          'Вложения привязаны к этой доске и карточке (строке), а не ко всем доскам одной строки таблицы.',
          'Клик по превью открывает просмотрщик; при наведении видно имя; ✕ открепляет файл, не удаляя его из галереи.',
          'Если файл в корзине, вложение остаётся с оверлеем «в корзине»; окончательное удаление файла снимает вложение.',
          'Прикреплять и откреплять могут все с доступом к пространству (viewer+).',
        ],
        where: 'Модалка карточки → вкладка «Файлы».',
      },
      {
        title: 'Колонки',
        items: [
          'Админ: добавить колонку (новое или существующее значение статуса), переименовать, изменить порядок или удалить.',
          'Колонку с карточками нельзя удалить — сначала перенесите или удалите карточки.',
          'Удаление колонки скрывает её только на этой доске; значение в справочнике остаётся.',
        ],
      },
      {
        title: 'Исполнители и дедлайны',
        items: [
          'Откройте карточку → «Исполнители»: добавьте участников пространства — у каждого свой дедлайн и целевая колонка.',
          'Назначать, менять дедлайн/колонку и приоритет (от самого низкого до самого высокого) могут только Владелец / Админ.',
          'Редактор видит всех исполнителей; Наблюдатель — только своё назначение на карточке.',
          'Перенос карточки в целевую колонку исполнителя завершает его задачу; перетаскивание назад открывает её снова.',
          'Несколько человек с одной целевой колонкой завершаются одним переносом.',
          'Просроченные назначения подсвечены на карточке; уведомления в колокольчик приходят при входе / обновлении токена (без фонового крона).',
          'Личные алерты — по вашим дедлайнам (поджимает / сегодня / просрочено). Владелец/Админ также получают digest, если просрочил кто-то другой.',
        ],
        where: 'Доска → модалка карточки → Исполнители (и Приоритет под полями).',
        tip: 'Смена дедлайна сбрасывает состояние алертов — следующий вход/refresh снова сможет уведомить.',
      },
      {
        title: 'Права доступа',
        items: [
          'Наблюдатель — просмотр, перетаскивание карточек, комментарии и файлы; видит только своих исполнителей.',
          'Редактор — создание, правка и удаление карточек (строк); видит всех исполнителей, но не назначает.',
          'Владелец / Админ — настройки доски, колонки, удаление доски, модерация комментариев, назначения, дедлайны/колонки и приоритет.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Процессы',
    intro: 'Процессы — отдельный продуктовый слой рядом с Файлами, Данными, Досками и Галереями, а не экран настроек. Вы собираете граф автоматизации, который гоняет файлы, строки и карточки по уже настроенной системе.',
    blocks: [
      {
        title: 'Это не настройки',
        items: [
          'Процессы живут в своей секции сайдбара — как Данные или Доски, а не внутри Профиля / настроек.',
          'Каждый процесс принадлежит пространству и использует его папки, таблицы, доски, паттерны и теги.',
          'Включение запускает процесс; Сохранить пишет граф. Запуски показывают, что реально произошло.',
        ],
        tip: 'Язык, тема и аккаунт — это Профиль, не Процессы.',
      },
      {
        title: 'Где найти процессы',
        items: [
          'Сайдбар → «Процессы» → «Все» — каталог по пространствам (/workflows).',
          'Сайдбар → «Процессы» → «Текущие» — быстрый список активного space (скрыт, если пусто).',
          '«Создать» под Процессами — новый граф (только owner/admin).',
          'Открыть процесс — конструктор графа; Запуски — журнал выполнений.',
        ],
        where: 'Левый сайдбар → Процессы.',
      },
      {
        title: 'Конструктор графа',
        items: [
          'Палитра — группы: Триггеры, Файлы, Содержимое, AI, Таблица, Доска, Поток; плюс панель шаблонов.',
          'Холст — узлы, связи, перетаскивание; Отменить / Повторить рядом с названием (также Ctrl+Z / Ctrl+Y).',
          'Инспектор — настройка выбранного триггера или ноды (папка, теги, таблица, расписание cron и т.д.).',
          'Ровно один триггер. Обычно цепочка; «Если / ветка» может разделить поток на True и False.',
          '«Проверить» смотрит обязательные поля и смысл потока (файлы / строки) до Сохранения.',
          'Клавиши: копировать / вставить ноды (Ctrl+C / Ctrl+V), удалить выделение (Delete / Backspace) — см. «Горячие клавиши».',
        ],
        tip: 'Cron: один раз, каждый день, каждую неделю (день + время) или каждый месяц (число + время), с учётом часового пояса.',
      },
      {
        title: 'Палитра: Триггеры',
        text: 'В процессе ровно один триггер.',
        items: [
          'Файл загружен — старт при загрузке файла в отслеживаемую папку.',
          'Строка добавлена — старт при появлении новой строки в таблице.',
          'Строки импортированы — старт после импорта CSV/Excel в таблицу.',
          'Ячейка совпала — старт, когда ячейка выбранной колонки меняется на подходящее значение.',
          'Файл перемещён — старт при переносе файла в отслеживаемую папку.',
          'По расписанию — старт по cron (поток сначала пустой).',
        ],
      },
      {
        title: 'Палитра: Файлы',
        items: [
          'Добавить теги — навешивает теги на файлы в потоке.',
          'Убрать теги — снимает теги с файлов в потоке.',
          'Переместить файл — переносит файлы в целевую папку.',
          'В корзину — отправляет файлы в корзину.',
          'Связанные строки — находит строки таблиц, связанные с файлами в потоке.',
        ],
      },
      {
        title: 'Палитра: Содержимое',
        items: [
          'Паттерн — накладывает паттерн пространства на scene каждого файла.',
          'Сохранить как — сохраняет копию каждого файла в целевую папку.',
          'Заменить содержимое — подменяет файл вырезкой из «Убрать фон».',
        ],
      },
      {
        title: 'Палитра: AI',
        items: [
          'Убрать фон — вырезает фон у каждого изображения (списывает AI-токены).',
          'Генерация изображения — создаёт новое изображение на каждый файл в потоке.',
        ],
        tip: 'AI-ноды списывают токены из общего пула в профиле.',
      },
      {
        title: 'Палитра: Таблица',
        items: [
          'Записать ячейки — записывает значения в выбранные колонки строк потока.',
          'Добавить строку — создаёт новую строку в таблице.',
          'Связанные файлы — загружает в поток файлы, связанные со строками.',
        ],
      },
      {
        title: 'Палитра: Доска',
        items: [
          'Добавить приоритет — ставит приоритет карточкам в потоке.',
          'Снять приоритет — убирает приоритет с карточек в потоке.',
          'Добавить назначение — назначает исполнителя (с дедлайном/колонкой) на карточки.',
          'Снять назначение — снимает исполнителей с карточек в потоке.',
        ],
      },
      {
        title: 'Палитра: Поток',
        items: [
          'Источник: таблица — подаёт строки таблицы в генерацию изображений (data-вход).',
          'Поиск — загружает файлы пространства в поток по тегам, имени или расширению.',
          'Фильтр — оставляет в потоке только подходящие элементы.',
          'Связка — сопоставляет файлы со значениями справочника по имени (опционально тег).',
          'Лимит — оставляет только первые N элементов потока.',
          'Если / ветка — направляет запуск в True или False, не меняя поток.',
        ],
      },
      {
        title: 'Кто что может (read-only для editor)',
        items: [
          'Owner / Admin — создавать, править, сохранять, включать/выключать, удалять и управлять шаблонами.',
          'Editor / Viewer — каталог и схема только для просмотра (без палитры и Save), плюс Запуски.',
        ],
        tip: 'Редактор может изучать, как процесс собран и как отработал — но не меняет автоматизацию.',
      },
      {
        title: 'Запуски / journal',
        items: [
          'Каждый запуск пишет триггер и шаги нод: ok, skipped или failed, с временем и деталями.',
          'Откройте запуск — граф с бейджами шагов и список шагов.',
          'Запуски доступны любому участнику пространства.',
        ],
        where: 'Процесс → Запуски или карточка каталога → Runs.',
      },
      {
        title: 'Пользовательские шаблоны',
        items: [
          'Owner / Admin: меню Save → «Сохранить как шаблон» — граф в библиотеку пространства.',
          'Панель шаблонов слева — применить шаблон на холст (копия с новыми id) или удалить шаблон.',
          'Применение не создаёт live-ссылку — дальше правите копию как обычный процесс.',
        ],
        tip: 'Шаблоны привязаны к пространству. Editor не создаёт и не применяет их.',
      },
    ],
  },
  notifications: {
    title: 'Уведомления и приглашения',
    intro: 'Колокольчик в верхней панели — центр входящих событий.',
    blocks: [
      {
        title: 'Вкладка «Комментарии»',
        items: [
          'Уведомления о новых комментариях и ответах в ваших файлах.',
          'Уведомления о комментариях к карточкам досок.',
          'Дедлайны: «поджимает» (24 ч), «сегодня», «просрочено» — если вы исполнитель.',
          'Digest для владельца/админа: кто-то другой на ваших досках просрочил (примерно раз в сутки на assignment).',
          'Нажмите на уведомление о файле — откроется изображение и вкладка «Комментарии».',
          'Нажмите на уведомление о карточке или дедлайне — откроется /boards/:id?card=:rowId с модалкой карточки.',
          '«Очистить все» — убрать все уведомления текущего пространства.',
        ],
        where: 'Колокольчик → вкладка с иконкой сообщения (открывается по умолчанию).',
      },
      {
        title: 'Вкладка «Входящие»',
        items: [
          'Приглашения присоединиться к чужому workspace.',
          'Принять или отклонить.',
          'Видны: кто пригласил, название пространства, роль, срок действия.',
        ],
      },
      {
        title: 'Вкладка «Отправленные»',
        items: [
          'Приглашения, которые вы отправили.',
          'Отменить ожидающее приглашение.',
          'Кнопка «Добавить участника» — пригласить нового пользователя.',
        ],
      },
      {
        title: 'Как пригласить пользователя',
        items: [
          'Колокольчик → «Отправленные» → «Добавить участника».',
          'Шаг 1: найти пользователя по email.',
          'Шаг 2: выбрать workspace и роль (Админ / Редактор / Наблюдатель). Ожидающие приглашения тоже занимают слот.',
          'Когда команда пространства заполнена, приглашение недоступно, пока системный админ не повысит лимит.',
          'Шаг 3: при необходимости скрыть папки, таблицы или доски, затем отправить приглашение.',
        ],
      },
      {
        title: 'Счётчик на колокольчике',
        text: 'Красный значок показывает сумму непрочитанных приглашений и комментариев для текущего пространства.',
      },
    ],
  },
  trash: {
    title: 'Корзина',
    intro: 'Удалённые файлы и папки попадают сюда, а не исчезают сразу.',
    blocks: [
      {
        title: 'Как открыть',
        items: [
          'Нажмите «Корзина» в панели инструментов на главном экране.',
          'Кнопка «Назад» в шапке корзины — вернуться в галерею.',
        ],
        where: 'Иконка корзины справа в панели инструментов.',
      },
      {
        title: 'Восстановление',
        items: [
          'Выделите элементы → контекстное меню → «Восстановить».',
          'Горячая клавиша: Ctrl+Alt+R.',
          'Элементы вернутся в исходное место.',
        ],
      },
      {
        title: 'Удаление навсегда',
        items: [
          'Кнопка × на карточке — удалить один файл или папку безвозвратно (только владелец).',
          '«Очистить корзину» — удалить всё содержимое (с подтверждением, только владелец).',
        ],
        tip: 'После очистки корзины восстановить файлы нельзя.',
      },
      {
        title: 'Ограничения в корзине',
        items: [
          'Нельзя загружать файлы и создавать папки.',
          'Нельзя перетаскивать элементы.',
          'Сортировка недоступна.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Горячие клавиши и жесты',
    intro: 'Шпаргалка для быстрой работы. Нажмите Shift+? (Shift+/) чтобы открыть её в любой момент. Не работают, когда открыт редактор изображений, курсор в поле ввода или процесс только для просмотра.',
    blocks: [
      {
        title: 'Галерея',
        items: [
          'Ctrl+A — выделить всё.',
          'Ctrl+U — загрузить файлы.',
          'Ctrl+Shift+М — вставить скриншот из буфера обмена.',
          'Ctrl+Alt+N — создать папку.',
          'Ctrl+X — вырезать.',
          'Ctrl+V — вставить.',
          'F2 — переименовать папку (одна в выделении).',
          'Ctrl+Alt+G — создать галерею (одна папка).',
          'Ctrl+Alt+T — добавить тег.',
          'Delete — удалить в корзину.',
          'Ctrl+Alt+R — восстановить (в корзине).',
          'Escape — снять выделение.',
        ],
      },
      {
        title: 'Просмотр файла',
        items: [
          '← / → — предыдущий / следующий файл.',
          'Escape — закрыть окно.',
        ],
      },
      {
        title: 'Редактор',
        items: [
          'Ctrl+Z (Ctrl+Я) — отменить.',
          'Ctrl+Y (Ctrl+Н) — повторить.',
          'Ctrl+0 — вписать в экран.',
          'Ctrl++ — увеличить масштаб.',
          'Ctrl+- — уменьшить масштаб.',
          'H (Р) — отразить по горизонтали (выбранное изображение).',
          'V (М) — отразить по вертикали (выбранное изображение).',
          'T (Е) — режим выпрямления.',
          'C (С) — свободная обрезка.',
          'L (Д) — лассо «удалить выделенное».',
          'Shift+L (Shift+Д) — лассо «оставить только выделенное».',
          'Ctrl+C (Ctrl+С) — копировать выбранный слой (не макет).',
          'Ctrl+V (Ctrl+М) — вставить слой.',
          'Delete / Backspace — удалить выбранный слой (текст, изображение или фигуру).',
          'Escape — выйти из текущего режима (пропорции макета, изменение размера, инструмент фигуры, обрезка, лассо) или сбросить изменения.',
        ],
        tip: 'Подсказки с клавишами есть в тултипах кнопок редактора (на компьютере с мышью). Пропорции макета и инструменты слоёв без буквенных клавиш — через панель свойств.',
      },
      {
        title: 'Таблицы (CRM-грид)',
        items: [
          'Enter — сохранить ячейку или начать редактирование.',
          'Escape — отменить редактирование ячейки.',
          'Tab / Shift+Tab — следующая / предыдущая ячейка.',
        ],
        where: 'Откройте любую таблицу из сайдбара.',
      },
      {
        title: 'Редактор процессов',
        items: [
          'Ctrl+Z — отменить.',
          'Ctrl+Y или Ctrl+Shift+Z — повторить.',
          'Ctrl+C — копировать выбранные ноды.',
          'Ctrl+V — вставить ноды.',
          'Delete / Backspace — удалить выбранные ноды.',
        ],
        where: 'Откройте процесс из сайдбара (Процессы).',
        tip: 'Работают по физическим клавишам (и на русской раскладке). Не активны, пока курсор в поле ввода или процесс только для просмотра.',
      },
      {
        title: 'Сенсорные жесты',
        items: [
          'Один тап — открыть; двойной тап — выделить.',
          'Рамка пальцем — массовое выделение.',
          'Долгое нажатие — контекстное меню.',
          'Pinch в редакторе — зум.',
        ],
      },
    ],
  },
};
